import { useEffect, useMemo, useState } from 'react'

import { Box, Card, Text } from '@factorialco/deprecated-design-system'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

interface Props {
  answers: { [key: string]: string }
}

// Helper function to parse initial employee IDs from either legacy or new format
const parseInitialIds = (data: string | undefined): string[] => {
  if (!data) return []

  // Try to parse as JSON (new format: { type: 'manual', ids: string[] } or { type: 'all', ... })
  try {
    const parsed = JSON.parse(data)
    if (typeof parsed === 'object' && parsed.type === 'manual' && Array.isArray(parsed.ids)) {
      return parsed.ids.map(String)
    }
    // For 'all' type, we can't provide initial IDs, start with empty selection
    return []
  } catch {
    // Legacy format: comma-separated string of IDs
    if (data === '') return []
    const parsedIds: string[] = JSON.parse('[' + data + ']').map(String)
    return parsedIds.filter((id) => id)
  }
}

export const TemporaryDisabilitiesToggle = ({ answers }: Props) => {
  const initialIds = useMemo(
    () => parseInitialIds(answers?.temporary_disabilities_managers),
    [answers?.temporary_disabilities_managers]
  )
  const [itsEnabled, setItsEnabled] = useState(answers?.its_enabled === 'true')

  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: initialIds,
  })

  const toggleIts = () => {
    answers['its_enabled'] = !itsEnabled ? 'true' : 'false'
    setItsEnabled(!itsEnabled)
  }

  // Send the full selector state to the backend as JSON string
  useEffect(() => {
    answers['temporary_disabilities_managers'] = JSON.stringify(employeeSelection)
  }, [employeeSelection, answers])

  return (
    <Box width="s400" padding="s16">
      <Card>
        <Card.Title size="small">
          {i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.absences.temporary_disabilities.title'
          )}
        </Card.Title>
        <Card.Description>
          {i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.absences.temporary_disabilities.description'
          )}
        </Card.Description>

        <Card.ExpandableContent expanded={itsEnabled} onExpandChange={toggleIts}>
          <Box flexDirection="row" justifyContent="flexStart" alignItems="center" gap="s4">
            <Text.Normal weight="semibold">
              {i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.absences.temporary_disabilities.admins'
              )}
            </Text.Normal>

            <InformationTooltip
              information={i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.absences.temporary_disabilities.tooltip'
              )}
              icon="info-circle"
              color="grey700"
            />
          </Box>
          <EmployeeSelectorV2
            multiple
            value={initialIds}
            onChange={setEmployeeSelection}
            options={{ hideSearchIcon: true }}
          />
        </Card.ExpandableContent>
      </Card>
    </Box>
  )
}
